import React from 'react';
import { Alert, Button, Spinner, formatServiceArea } from '@/components/ui';
import { KPIHomeWidget } from './KPIHomeWidget';
import type { RedirectConfig } from './ParentWidget';
import type { EquipmentHomeSummary } from '../../services/vehicleDefectsService';

/** "/equipment?serviceArea=16&overduePm=1" -> { path, queryParams } for the tile's redirect. */
export function redirectOf(href: string): RedirectConfig {
  const url = new URL(href, 'http://wrap.local');
  return { path: url.pathname, queryParams: Object.fromEntries(url.searchParams.entries()) };
}

export interface EquipmentHomeWidgetProps {
  summary: EquipmentHomeSummary | null;
  loading: boolean;
  error: Error | null;
  onRetry: () => void;
  requiredPermission: string;
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/**
 * V3-060: one row of count tiles per service area – defects reported in the last 24 hours, PMs
 * overdue and greasing overdue. Each opens its list filtered to that area with the same number:
 * the Open Defects report (last 24 hours) and the Fleet list (Overdue PM / Overdue Greasing).
 * Counts only, no lists (DASH-01).
 */
const EquipmentHomeWidget: React.FC<EquipmentHomeWidgetProps> = ({ summary, loading, error, onRetry, requiredPermission }) => {
  if (loading && !summary) {
    return (
      <div className="tw-flex tw-items-center tw-gap-2 tw-py-2" role="status">
        <Spinner animation="border" size="sm" />
        <span className="tw-text-sm tw-text-content-muted">Loading equipment counts…</span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="tw-mb-0 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
        <span>Equipment counts could not be loaded. {error.message}</span>
        <Button size="sm" variant="outline-danger" onClick={onRetry}>
          Retry
        </Button>
      </Alert>
    );
  }

  const areas = summary?.areas ?? [];
  if (areas.length === 0) {
    return <p className="tw-mb-0 tw-text-sm tw-text-content-muted">No service areas to show.</p>;
  }

  return (
    <div className="tw-flex tw-flex-col tw-gap-3" data-testid="equipment-home-tiles">
      {areas.map((area) => {
        const label = formatServiceArea(area.serviceArea) ?? area.serviceArea.name;
        return (
          <section key={area.serviceArea.id} aria-label={label}>
            <h3 className="tw-mb-1 tw-text-sm tw-font-semibold tw-text-content-secondary">{label}</h3>
            <div className="tw-grid tw-grid-cols-1 tw-gap-2 sm:tw-grid-cols-3">
              <KPIHomeWidget
                id={`equipment-recent-defects-${area.serviceArea.id}`}
                title="Defects in the Last 24 Hours"
                size="small"
                requiredPermission={requiredPermission}
                data={{ value: area.recentDefects, description: plural(area.recentDefects, 'Defect reported', 'Defects reported') }}
                redirect={redirectOf(area.links.recentDefects)}
                metadata={{ loading: false, error: null, isEmpty: false }}
                onRefresh={onRetry}
              />
              <KPIHomeWidget
                id={`equipment-overdue-pm-${area.serviceArea.id}`}
                title="Overdue PMs"
                size="small"
                requiredPermission={requiredPermission}
                data={{ value: area.overduePm, description: plural(area.overduePm, 'Unit overdue for a PM', 'Units overdue for a PM') }}
                redirect={redirectOf(area.links.overduePm)}
                metadata={{ loading: false, error: null, isEmpty: false }}
                onRefresh={onRetry}
              />
              <KPIHomeWidget
                id={`equipment-overdue-greasing-${area.serviceArea.id}`}
                title="Overdue Greasing"
                size="small"
                requiredPermission={requiredPermission}
                data={{
                  value: area.overdueGreasing,
                  description: plural(area.overdueGreasing, 'Unit overdue for greasing', 'Units overdue for greasing')
                }}
                redirect={redirectOf(area.links.overdueGreasing)}
                metadata={{ loading: false, error: null, isEmpty: false }}
                onRefresh={onRetry}
              />
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default EquipmentHomeWidget;
